import { useCallback, useContext } from "react";
import { Feature } from "geojson";
import uniqBy from "lodash/uniqBy";

import { isAssignmentCable } from "fond/draw/modes/planner/dropAssignments/helper";
import { AssignmentCableSystemFeature } from "fond/draw/modes/planner/dropAssignments/types";
import { inputLayerGroups, LayerIds } from "fond/layers";
import { MapContext } from "fond/map/MapProvider";
import { closeEditing, saveEditing } from "fond/project/redux";
import * as turf from "fond/turf";
import { featureCollection } from "fond/turf";
import { isValuePoint } from "fond/types/geojson";
import { getLineStringEndpoints } from "fond/utils/feature";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";

/** Return a callback that saves the features drawn on the map for a layer group and closes editing. */
export const useSaveLayerDrawing = (): ((inputLayerGroupId: string, onSaved?: () => void) => Promise<void>) => {
  const dispatch = useAppDispatch();
  const { drawControl, isDirty: isDrawingDirty, setDrawMode } = useContext(MapContext);
  const data = useAppSelector((state) => state.project.projects[state.project.projectId]?.data);

  return useCallback(
    /**
     * @param inputLayerGroupId the layer group being drawn.
     * @param onSaved called once the features are saved, before editing closes.
     */
    async (inputLayerGroupId: string, onSaved?: () => void) => {
      const layerGroupIds = inputLayerGroups[inputLayerGroupId].layers.map(({ id }) => id);
      let features: Feature[] = layerGroupIds.reduce(
        (currFeatures: Feature[], layerId) => [
          ...currFeatures,
          ...(drawControl.current?.getAll().features.filter((f) => f.properties?.layerId === layerId) || []),
        ],
        []
      );

      // When editing hubs we only edit a portion of all existing hubs, however we need
      // to pass the entire hub feature list otherwise backend will report an error
      if (inputLayerGroupId === "hub") {
        features = uniqBy([...features, ...(data?.layers?.["output/hub"]?.features ?? [])], "id");
        features = features.map((f) => ({ ...f, id: f.properties?.featureId }));
      }

      // For drop assignments we need to create a 'fake' assignment for each drop cable that wasn't reassigned by
      // the user.
      if (inputLayerGroupId === "dropAssignments") {
        const assignedLocations = new Set<string>();
        const groupIds = new Map<string, string>();

        const getGroupId = (key: string): string => {
          const currentGroupId = groupIds.get(key);
          if (currentGroupId !== undefined) return currentGroupId;

          const value = (groupIds.size + 1).toString(); // avoid zero; might be confused for null
          groupIds.set(key, value);
          return value;
        };

        // Record any assigned service locations
        const assignmentCables = (features as AssignmentCableSystemFeature[]).filter(isAssignmentCable);
        features = assignmentCables.flatMap((assignment) => {
          const { sinkGroup } = assignment.properties.originalCable.properties;
          const { geometry } = turf.lineString(getLineStringEndpoints(assignment));
          const assignmentGroupId = getGroupId(assignment.properties.source.properties.fondId);

          return sinkGroup.properties.serviceLocationIds.map((id) => {
            assignedLocations.add(id);
            return turf.feature(geometry, {
              layerId: LayerIds.dropAssignment,
              type: "user_input",
              GroupID: assignmentGroupId,
            });
          });
        });

        const dropCables = (data?.layers?.["output/fibre_cable"]?.features ?? [])
          .filter((f) => f.properties?.Tier === "DROP" && !assignedLocations.has(f.properties?.EndID))
          .map((f) => {
            // Truncate assignment to the endpoints; the actual path is irrelevant
            const geometry = f.geometry.type === "LineString" ? turf.lineString(getLineStringEndpoints(f)).geometry : f.geometry;
            return turf.feature(geometry, {
              layerId: LayerIds.dropAssignment,
              type: "system_input",
              GroupID: getGroupId(f.properties?.StartID),
            });
          });

        features.push(...dropCables);
      }

      // Workaround https://github.com/mapbox/mapbox-gl-draw/issues/774
      features = features.filter(isValuePoint);

      await dispatch(saveEditing(featureCollection(features), [], "draw-panel"));
      onSaved?.();

      if (isDrawingDirty) {
        setDrawMode("no_feature");
      }

      dispatch(closeEditing());
    },
    [data, dispatch, drawControl, isDrawingDirty, setDrawMode]
  );
};
